import { m } from "@vega-core/paraglide/messages";

import { assertNever } from "./assertNever";

// Define a type for the base keys.
type TimeDurationBaseKey = "dates.day" | "dates.month" | "dates.year";

/**
 * Returns the correct translation key for a time duration, based on the count.
 *
 * @param {number} count - The numerical value.
 * @param {TimeDurationBaseKey} baseKey - The base translation key.
 * @returns {string} The correct key (singular or plural).
 */
export function formatTimeDuration(
  count: number,
  baseKey: TimeDurationBaseKey,
): string {
  const isPlural = count !== 1;

  switch (baseKey) {
    case "dates.day":
      return isPlural ? m.dates_days({ count }) : m.dates_day({ count });
    case "dates.month":
      return isPlural ? m.dates_months({ count }) : m.dates_month({ count });
    case "dates.year":
      return isPlural ? m.dates_years({ count }) : m.dates_year({ count });
    default:
      assertNever(baseKey);
  }
}

/**
 * Turn rich text formatted text into an array of React nodes, since ParaglideJS does not support rich text formatting yet: https://github.com/opral/inlang/issues/3801#issuecomment-3707341283.
 * @example
 * richTextFormat("This is a <link>page</link>", { link: (children) => <Link to="/path">{children}</Link> })
 * // This will become
 * ["This is a ", <Link to="/path">page</Link>, "."]
 */
export function richTextFormat(
  text: string,
  elements: Record<
    string,
    ((props: { children: string }) => React.ReactNode) | undefined
  >,
): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  let lastIndex = 0;

  const tagRegex = new RegExp("<(\\w+)>(.*?)</\\1>", "g");

  text.replace(tagRegex, (match, tag, content, offset) => {
    // Add text before the match
    if (offset > lastIndex) {
      parts.push(text.slice(lastIndex, offset));
    }

    // Add the React component if the key exists in elements
    const Element = elements[tag];

    if (Element) {
      parts.push(Element({ children: content }));
    } else {
      // If no matching element, keep the original text
      parts.push(match);
    }

    lastIndex = offset + match.length;

    return match;
  });

  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }

  return parts;
}
